import React from "react";
import Linkify from "linkify-react";
import mdParser from "~/utils/markdown";

/**
 * 检测文本是否为 HTML 格式
 */
function isHtml(text) {
  if (!text || typeof text !== "string") return false;
  return /<[a-z][\s\S]*>/i.test(text);
}

/**
 * 检测文本是否包含 Markdown 语法
 */
function isMarkdown(text) {
  if (!text || typeof text !== "string") return false;

  // 如果已经是 HTML，不需要当作 markdown 处理
  if (isHtml(text)) return false;

  // 检测明确的 markdown 语法特征
  const strongMarkdownPatterns = [
    /^#{1,6}\s+.+/m, // 标题 (# ## ###)
    /^\s*[-*+]\s+.+/m, // 无序列表 (- * +)
    /^\s*\d+\.\s+.+/m, // 有序列表 (1. 2. 3.)
    /\[.+\]\(.+\)/, // 链接 [text](url)
    /!\[.+\]\(.+\)/, // 图片 ![alt](url)
    /```[\s\S]+```/, // 代码块 ```code```
    /^\s*>/, // 引用 >
    /^\s*\|.+\|/m, // 表格 |col|col|
  ];

  // 如果包含明确的 markdown 语法，直接返回 true
  if (strongMarkdownPatterns.some((pattern) => pattern.test(text))) {
    return true;
  }

  // 检测格式化语法（需要更严格的判断，避免误判）
  const hasBold = /\*\*[^*]+\*\*/.test(text) || /_{2}[^_]+_{2}/.test(text);
  const hasInlineCode = /`[^`]+`/.test(text);
  const hasStrikethrough = /~{2}[^~]+~{2}/.test(text);

  // 只有包含明确的格式化语法才认为是 markdown
  return hasBold || hasInlineCode || hasStrikethrough;
}

/**
 * SmartContent 组件
 * 自动检测文本类型并以合适的方式渲染
 *
 * @param {string} text - 要渲染的文本内容
 * @param {string} className - 自定义 className
 * @param {object} style - 自定义样式
 * @param {object} linkifyOptions - Linkify 组件的配置项
 */
export default function SmartContent(props) {
  const {
    text = "",
    className = "",
    style = {},
    linkifyOptions = {
      className: "linkify-text",
      target: "_blank",
    },
  } = props;

  if (!text) return null;

  // 如果是 HTML 格式，直接渲染
  if (isHtml(text)) {
    return (
      <span
        className={`smart-content smart-content-html ${className}`}
        style={{ display: "inline", ...style }}
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  }

  // 如果是 Markdown 格式，先转换为 HTML 再渲染
  if (isMarkdown(text)) {
    const html = mdParser.render(text);
    return (
      <span
        className={`smart-content smart-content-markdown ${className}`}
        style={{ display: "inline", ...style }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  // 否则是纯文本，使用 Linkify
  return (
    <span
      className={`smart-content smart-content-plain ${className}`}
      style={{ display: "inline", ...style }}
    >
      <Linkify options={linkifyOptions}>{text}</Linkify>
    </span>
  );
}
